import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';

/**
 * Get all system announcements (for admin management)
 */
export const useAllSystemAnnouncements = (options = {}) => {
  return useQuery({
    queryKey: ['system-announcements', 'all'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('system_announcements')
        .select('*, creator:app_users!system_announcements_created_by_fkey(first_name, last_name)')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    ...options
  });
};

/**
 * Get active system announcements for the current user
 */
export const useActiveSystemAnnouncements = (userRole, options = {}) => {
  return useQuery({
    queryKey: ['system-announcements', 'active', userRole],
    queryFn: async () => {
      const now = new Date().toISOString();

      let query = supabase
        .from('system_announcements')
        .select('*')
        .eq('is_active', true)
        .or(`start_at.is.null,start_at.lte.${now}`)
        .or(`end_at.is.null,end_at.gt.${now}`)
        .order('type', { ascending: false }) // critical first
        .order('created_at', { ascending: false });

      const { data, error } = await query;

      if (error) throw error;

      // Filter by role on client side (simpler than complex SQL)
      return (data || []).filter(announcement => {
        if (!announcement.show_to_roles || announcement.show_to_roles.length === 0) {
          return true; // Show to all
        }
        return announcement.show_to_roles.includes(userRole);
      });
    },
    refetchInterval: 60000, // Refetch every minute to catch schedule changes
    ...options
  });
};

/**
 * Create a new system announcement
 */
export const useCreateSystemAnnouncement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (announcementData) => {
      const { data, error } = await supabase
        .from('system_announcements')
        .insert(announcementData)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system-announcements'] });
      toast.success('Ogłoszenie systemowe zostało utworzone');
    },
    onError: (error) => {
      logger.error('Create system announcement error', { message: error.message });
      toast.error(`Nie udało się utworzyć ogłoszenia: ${error.message}`);
    }
  });
};

/**
 * Update a system announcement
 */
export const useUpdateSystemAnnouncement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }) => {
      const { data, error } = await supabase
        .from('system_announcements')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system-announcements'] });
      toast.success('Ogłoszenie zostało zaktualizowane');
    },
    onError: (error) => {
      logger.error('Update system announcement error', { message: error.message });
      toast.error(`Nie udało się zaktualizować ogłoszenia: ${error.message}`);
    }
  });
};

/**
 * Toggle announcement active state
 */
export const useToggleSystemAnnouncement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, is_active }) => {
      const { data, error } = await supabase
        .from('system_announcements')
        .update({ is_active, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['system-announcements'] });
      toast.success(data.is_active ? 'Ogłoszenie aktywowane' : 'Ogłoszenie dezaktywowane');
    },
    onError: (error) => {
      logger.error('Toggle system announcement error', { message: error.message });
      toast.error(`Nie udało się zmienić statusu: ${error.message}`);
    }
  });
};

/**
 * Delete a system announcement
 */
export const useDeleteSystemAnnouncement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const { error } = await supabase
        .from('system_announcements')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system-announcements'] });
      toast.success('Ogłoszenie zostało usunięte');
    },
    onError: (error) => {
      logger.error('Delete system announcement error', { message: error.message });
      toast.error(`Nie udało się usunąć ogłoszenia: ${error.message}`);
    }
  });
};
